import { getApiBaseUrl } from '../utils/config';

export interface PaymentRequest {
  price_id: string;
  customer_email: string;
  customer_name: string;
  company?: string;
  phone?: string;
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
}

export interface SubscriptionStatus {
  status: string;
  plan_name?: string;
  current_period_end?: string;
  amount?: number;
  currency?: string;
}

export interface EarlyAccessCount {
  total_purchased: number;
  remaining_spots: number;
  max_spots: number;
  spots_until_price_increase: number;
  current_price: number;
  next_price: number;
  timestamp: string;
}

export interface PricingTier {
  price: number;
  price_id: string;
  spots_until_increase: number;
  tier_max: number;  // Required since backend always sends it
  tier_min: number;  // Starting point of the tier (e.g., 0 for tier 1, 10 for tier 2)
  tier_capacity: number;  // How many spots in this tier (e.g., 10 for each tier)
  tier_sold: number;  // How many sold within this tier (0-10)
}

export interface PricingConfig {
  available: boolean;
  total_purchased: number;
  remaining_spots: number;  // Spots remaining in current tier
  total_remaining_spots?: number;  // Total spots across all tiers
  max_spots: number;
  current_tier: PricingTier;
  next_tier?: {
    price: number;
    spots_until_increase: number;
  };
  timestamp: string;
  message?: string;
}

export interface EarlyAccessUpdate {
  type: 'early_access_update';
  data: EarlyAccessCount;
  timestamp: string;
}

class PaymentService {
  private baseUrl = getApiBaseUrl();
  // TODO: WebSocket implementation for real-time updates
  // private wsConnection: WebSocket | null = null;
  // private earlyAccessListeners: Set<(data: EarlyAccessCount) => void> = new Set();

  async createCheckoutSession(paymentRequest: PaymentRequest): Promise<CheckoutResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentRequest),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  async getPricingConfig(): Promise<PricingConfig> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/payment/pricing-config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get pricing configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting pricing configuration:', error);
      throw error;
    }
  }

  async getEarlyAccessCount(): Promise<EarlyAccessCount> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/payment/early-access-count`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get early access count');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting early access count:', error);
      throw error;
    }
  }

  // TODO: WebSocket implementation for real-time early access updates
  /*
  connectToEarlyAccessUpdates(): void {
    // Don't create multiple connections
    if (this.wsConnection && this.wsConnection.readyState === WebSocket.OPEN) {
      return;
    }

    const wsUrl = this.baseUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    this.wsConnection = new WebSocket(`${wsUrl}/api/v1/payment/ws/early-access-count`);

    this.wsConnection.onopen = () => {
      console.log('Connected to early access count WebSocket');
    };

    this.wsConnection.onmessage = (event) => {
      try {
        const update: EarlyAccessUpdate = JSON.parse(event.data);
        if (update.type === 'early_access_update') {
          // Notify all listeners
          this.earlyAccessListeners.forEach(listener => {
            listener(update.data);
          });
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.wsConnection.onclose = () => {
      console.log('Early access count WebSocket connection closed');
      // Attempt to reconnect after 5 seconds
      setTimeout(() => {
        if (this.earlyAccessListeners.size > 0) {
          this.connectToEarlyAccessUpdates();
        }
      }, 5000);
    };

    this.wsConnection.onerror = (error) => {
      console.error('Early access count WebSocket error:', error);
    };
  }

  subscribeToEarlyAccessUpdates(callback: (data: EarlyAccessCount) => void): () => void {
    this.earlyAccessListeners.add(callback);
    
    // Connect to WebSocket if not already connected
    this.connectToEarlyAccessUpdates();

    // Return unsubscribe function
    return () => {
      this.earlyAccessListeners.delete(callback);
      
      // Close WebSocket if no more listeners
      if (this.earlyAccessListeners.size === 0 && this.wsConnection) {
        this.wsConnection.close();
        this.wsConnection = null;
      }
    };
  }

  disconnectEarlyAccessUpdates(): void {
    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }
    this.earlyAccessListeners.clear();
  }
  */

  async getSubscriptionStatus(userId: string, token: string): Promise<SubscriptionStatus> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/payment/subscription-status/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get subscription status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  async cancelSubscription(token: string): Promise<{ message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/payment/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  async createBillingPortalSession(token: string): Promise<{ success: boolean; portal_url: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/payment/create-billing-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create billing portal session');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      throw error;
    }
  }

  // Helper method to get URL parameters (for handling success/cancel redirects)
  getUrlParameter(name: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  // Helper method to handle post-payment redirect logic
  handlePaymentRedirect(): { status: 'success' | 'canceled' | null; sessionId?: string } {
    const paymentStatus = this.getUrlParameter('payment');
    const sessionId = this.getUrlParameter('session_id');

    if (paymentStatus === 'success') {
      return { status: 'success', sessionId: sessionId || undefined };
    } else if (paymentStatus === 'canceled') {
      return { status: 'canceled' };
    }

    return { status: null };
  }
}

export const paymentService = new PaymentService();